// ABOUTME: "Your bulk change" column of the Scope and Actions steps: the scope (count, path, first objects) and the
// ABOUTME: actions as numbered sentences, updated while the user edits; the step being edited is marked "Editing".

import { Box, Button, Chip, CircularProgress, Divider, Stack, Typography } from '@ds/coolshop';
import { useIntl } from 'react-intl';

import { ScopePreview } from '../types';

type RecapSidebarProps = {
    editing: 'scope' | 'actions' | null;
    preview: ScopePreview | null;
    previewLoading: boolean;
    hierarchyLabel: string;
    branchLabel?: string;
    /** The field names of the scope conditions. */
    conditionLabels: string[];
    /** One sentence per action (describeAction). */
    actionSentences: string[];
    onEditScope?: () => void;
    onOpenInListView?: () => void;
    /** The scope is incomplete or its list path is still being resolved. */
    openInListViewDisabled?: boolean;
};

/** Values shown while the scope is counted again: still readable, visibly not current. */
const STALE_OPACITY = 0.4;

function StepHeader({
    label,
    editing,
    loading = false,
}: {
    label: string;
    editing: boolean;
    loading?: boolean;
}) {
    const { formatMessage } = useIntl();
    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="overline" color="text.secondary">
                {label}
            </Typography>
            <Stack direction="row" alignItems="center" gap={1}>
                {loading && (
                    <Box display="flex" data-testid="bulk-change-recap-loading">
                        <CircularProgress size="small" />
                    </Box>
                )}
                {editing && (
                    <Chip
                        size="small"
                        color="primary"
                        label={formatMessage({
                            id: 'bulkChange.recap.editing',
                            defaultMessage: 'Editing',
                        })}
                    />
                )}
            </Stack>
        </Stack>
    );
}

export function RecapSidebar({
    editing,
    preview,
    previewLoading,
    hierarchyLabel,
    branchLabel,
    conditionLabels,
    actionSentences,
    onEditScope,
    onOpenInListView,
    openInListViewDisabled = false,
}: RecapSidebarProps) {
    const intl = useIntl();
    const { formatMessage } = intl;
    const path = [hierarchyLabel, branchLabel].filter(Boolean).join(' · ');
    const count = preview?.count;

    const scopeCard =
        editing === 'scope' ? (
            <Box
                p={2}
                border={1}
                borderColor="divider"
                borderRadius={2}
                bgcolor="background.paper"
                data-testid="bulk-change-recap-scope"
            >
                <Stack gap={2}>
                    <StepHeader
                        label={formatMessage({
                            id: 'bulkChange.recap.scopeStep',
                            defaultMessage: 'Step 1 · Scope',
                        })}
                        editing
                        loading={previewLoading}
                    />
                    {/* While the scope is counted again the last answer stays, greyed out. */}
                    <Box
                        opacity={previewLoading ? STALE_OPACITY : 1}
                        data-testid="bulk-change-recap-scope-values"
                    >
                        <Stack gap={2}>
                            <Typography variant="h4" data-testid="bulk-change-recap-count">
                                {count === undefined
                                    ? '…'
                                    : formatMessage(
                                          {
                                              id: 'bulkChange.recap.objects',
                                              defaultMessage:
                                                  '{count, plural, one {# object} other {# objects}}',
                                          },
                                          { count },
                                      )}
                            </Typography>
                            {preview && (
                                <Typography variant="body2" color="text.secondary">
                                    {formatMessage(
                                        {
                                            id: 'bulkChange.recap.ofTotal',
                                            defaultMessage: 'of {total, number} in {path}',
                                        },
                                        { total: preview.totalInRange, path },
                                    )}
                                </Typography>
                            )}
                            {preview && preview.nodes.length > 0 && (
                                <>
                                    <Divider />
                                    <Stack gap={1.5}>
                                        {preview.nodes.map(node => (
                                            <Stack key={node.id} gap={0.25}>
                                                {node.breadcrumb.length > 0 && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {node.breadcrumb.join(' › ')}
                                                    </Typography>
                                                )}
                                                <Typography variant="subtitle2">
                                                    {node.listName && node.listName !== node.code
                                                        ? `${node.code} · ${node.listName}`
                                                        : node.code}
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <Typography variant="caption" color="text.secondary">
                                            {formatMessage(
                                                {
                                                    id: 'bulkChange.recap.showing',
                                                    defaultMessage:
                                                        'Showing {shown} of {count, number}',
                                                },
                                                {
                                                    shown: preview.nodes.length,
                                                    count: preview.count,
                                                },
                                            )}
                                        </Typography>
                                        {onOpenInListView && (
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={onOpenInListView}
                                                disabled={openInListViewDisabled}
                                                data-testid="bulk-change-open-in-list-view"
                                            >
                                                {formatMessage({
                                                    id: 'bulkChange.recap.openInListView',
                                                    defaultMessage: 'Open in list view',
                                                })}
                                            </Button>
                                        )}
                                    </Stack>
                                </>
                            )}
                        </Stack>
                    </Box>
                </Stack>
            </Box>
        ) : (
            <Box
                p={2}
                border={1}
                borderColor="divider"
                borderRadius={2}
                bgcolor="background.paper"
                data-testid="bulk-change-recap-scope"
            >
                <Stack gap={1}>
                    <StepHeader
                        label={formatMessage({
                            id: 'bulkChange.recap.scopeStep',
                            defaultMessage: 'Step 1 · Scope',
                        })}
                        editing={false}
                        loading={previewLoading}
                    />
                    {/* Not counted yet (a dialog opened on a run) reads "…", never "0 objects". */}
                    <Box
                        opacity={previewLoading ? STALE_OPACITY : 1}
                        data-testid="bulk-change-recap-scope-values"
                    >
                        <Typography variant="body2" data-testid="bulk-change-recap-summary">
                            {count === undefined
                                ? '…'
                                : formatMessage(
                                      {
                                          id: 'bulkChange.recap.scopeSummary',
                                          defaultMessage:
                                              '{count, plural, one {# object} other {# objects}} in {path}',
                                      },
                                      { count, path },
                                  )}
                        </Typography>
                    </Box>
                    {conditionLabels.length > 0 && (
                        <Typography variant="body2">
                            {formatMessage(
                                {
                                    id: 'bulkChange.recap.conditions',
                                    defaultMessage:
                                        '{count, plural, one {# condition} other {# conditions}}: {fields}',
                                },
                                {
                                    count: conditionLabels.length,
                                    fields: conditionLabels.join(', '),
                                },
                            )}
                        </Typography>
                    )}
                    {onEditScope && (
                        <Box>
                            <Button variant="text" size="small" onClick={onEditScope}>
                                {formatMessage({
                                    id: 'bulkChange.recap.editScope',
                                    defaultMessage: 'Edit scope',
                                })}
                            </Button>
                        </Box>
                    )}
                </Stack>
            </Box>
        );

    const actionsCard =
        actionSentences.length === 0 ? (
            <Box
                p={2}
                border="1px dashed"
                borderColor="divider"
                borderRadius={2}
                data-testid="bulk-change-recap-actions"
            >
                <Stack gap={1}>
                    <StepHeader
                        label={formatMessage({
                            id: 'bulkChange.recap.actionsStep',
                            defaultMessage: 'Step 2 · Actions',
                        })}
                        editing={editing === 'actions'}
                    />
                    <Typography variant="body2" color="text.secondary">
                        {formatMessage({
                            id: 'bulkChange.recap.notDefined',
                            defaultMessage: 'Not defined yet',
                        })}
                    </Typography>
                </Stack>
            </Box>
        ) : (
            <Box
                p={2}
                border={1}
                borderColor="divider"
                borderRadius={2}
                bgcolor="background.paper"
                data-testid="bulk-change-recap-actions"
            >
                <Stack gap={2}>
                    <StepHeader
                        label={formatMessage({
                            id: 'bulkChange.recap.actionsStep',
                            defaultMessage: 'Step 2 · Actions',
                        })}
                        editing={editing === 'actions'}
                    />
                    <Stack gap={1.5}>
                        {actionSentences.map((sentence, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <Stack key={index} direction="row" gap={1}>
                                <Typography variant="body2">{index + 1}</Typography>
                                <Typography variant="body2">{sentence}</Typography>
                            </Stack>
                        ))}
                    </Stack>
                    {/* Only several actions have an order to speak of (a delete is always alone). */}
                    {actionSentences.length > 1 && (
                        <>
                            <Divider />
                            <Typography variant="caption" color="text.secondary">
                                {formatMessage({
                                    id: 'bulkChange.recap.orderNote',
                                    defaultMessage:
                                        'Each action targets a different field and language, so their order never matters.',
                                })}
                            </Typography>
                        </>
                    )}
                </Stack>
            </Box>
        );

    return (
        <Stack gap={2}>
            <Stack gap={1}>
                <Typography variant="subtitle2">
                    {formatMessage({
                        id: 'bulkChange.recap.title',
                        defaultMessage: 'Your bulk change',
                    })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {formatMessage({
                        id: 'bulkChange.recap.subtitle',
                        defaultMessage:
                            'A recap of each step. It updates while you edit on the left.',
                    })}
                </Typography>
            </Stack>
            {scopeCard}
            {actionsCard}
        </Stack>
    );
}
